import { useEffect, useState } from 'react';
import { DollarSign, ShoppingBag, TrendingUp, Mail, AlertTriangle, CalendarRange, X } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { adminSalesAPI } from '../../services/api';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const money = (n, ccy = 'AUD') => {
  try { return `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`; }
  catch { return `${n}`; }
};

const formatMonthLabel = (key) => {
  const [y, m] = (key || '').split('-');
  const mi = Number(m) - 1;
  if (Number.isNaN(mi) || mi < 0 || mi > 11) return key;
  return `${MONTH_NAMES[mi]} '${(y || '').slice(2)}`;
};

const formatPrettyDate = (iso) => {
  // iso = YYYY-MM-DD
  const [y, m, d] = (iso || '').split('-');
  const mi = Number(m) - 1;
  if (!y || Number.isNaN(mi) || mi < 0 || mi > 11) return iso;
  return `${Number(d)} ${MONTH_NAMES[mi]} ${y}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'blue', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className={`text-3xl font-bold text-${color}-600 mt-1 break-words`}>{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50 flex-shrink-0`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SalesAdmin = () => {
  const [months, setMonths] = useState(12);
  // Custom date range. `appliedRange` drives the fetch; the two `range*Draft`
  // values are bound to the date inputs so the user can adjust dates before
  // committing them via Apply.
  const [rangeStartDraft, setRangeStartDraft] = useState(daysAgoIso(30));
  const [rangeEndDraft, setRangeEndDraft] = useState(todayIso());
  const [appliedRange, setAppliedRange] = useState(null); // null | { start, end }
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) setLoading(true);
        const args = appliedRange
          ? { start: appliedRange.start, end: appliedRange.end }
          : { months };
        const d = await adminSalesAPI.summary(args);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [months, appliedRange]);

  if (loading && !data) return <div className="p-8 text-center text-gray-500">Loading sales analytics…</div>;
  if (!data) return <div className="p-8 text-center text-red-600">Failed to load analytics</div>;

  const primaryCurrency = (data.currency_split[0]?.currency) || 'AUD';
  const monthlyChart = data.monthly_series.map((m) => ({
    month: formatMonthLabel(m.month),
    Revenue: m.revenue,
    Orders: m.orders,
  }));
  const chartTitle = appliedRange
    ? `Monthly revenue · ${formatPrettyDate(appliedRange.start)} → ${formatPrettyDate(appliedRange.end)}`
    : `Monthly revenue · last ${months} month${months === 1 ? '' : 's'}`;
  const periodLabel = appliedRange
    ? `${formatPrettyDate(appliedRange.start)} → ${formatPrettyDate(appliedRange.end)}`
    : null;
  const revenueSub = periodLabel || 'All-time paid orders';
  const ordersSub = periodLabel || 'All-time';

  const rangeInvalid = !rangeStartDraft || !rangeEndDraft || rangeStartDraft > rangeEndDraft;
  const applyRange = () => {
    if (rangeInvalid) return;
    setAppliedRange({ start: rangeStartDraft, end: rangeEndDraft });
  };
  const clearRange = () => setAppliedRange(null);

  return (
    <div className="space-y-6" data-testid="sales-admin">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Sales Analytics</h2>
          <p className="text-gray-600 text-sm mt-1">
            Aggregated metrics from paid Stripe orders.
            {!data.email_service_live && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded text-xs">
                <Mail className="h-3 w-3" /> Email service inactive (RESEND_API_KEY not set)
              </span>
            )}
          </p>
        </div>
        <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))} disabled={!!appliedRange}>
          <SelectTrigger className="w-44" data-testid="sales-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
              <SelectItem key={n} value={String(n)} data-testid={`sales-period-${n}m`}>
                {n === 1 ? 'Last 1 month' : `Last ${n} months`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Custom date range */}
      <Card data-testid="sales-range-card">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 lg:mb-2">
              <CalendarRange className="h-4 w-4 text-blue-600" />
              Custom date range
              {appliedRange && (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                  {formatPrettyDate(appliedRange.start)} → {formatPrettyDate(appliedRange.end)}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3 flex-1">
              <div className="flex flex-col gap-1">
                <label htmlFor="range-start" className="text-xs uppercase tracking-wide text-gray-500">Start</label>
                <Input
                  id="range-start"
                  type="date"
                  className="w-44"
                  max={rangeEndDraft || undefined}
                  value={rangeStartDraft}
                  onChange={(e) => setRangeStartDraft(e.target.value)}
                  data-testid="sales-range-start"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="range-end" className="text-xs uppercase tracking-wide text-gray-500">End</label>
                <Input
                  id="range-end"
                  type="date"
                  className="w-44"
                  min={rangeStartDraft || undefined}
                  max={todayIso()}
                  value={rangeEndDraft}
                  onChange={(e) => setRangeEndDraft(e.target.value)}
                  data-testid="sales-range-end"
                />
              </div>
              <Button
                onClick={applyRange}
                disabled={rangeInvalid}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="sales-range-apply"
              >
                Apply range
              </Button>
              {appliedRange && (
                <Button
                  variant="outline"
                  onClick={clearRange}
                  data-testid="sales-range-clear"
                >
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>
          {rangeInvalid && (rangeStartDraft || rangeEndDraft) && (
            <p className="text-xs text-red-600 mt-2" data-testid="sales-range-error">
              Start date must be on or before end date.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={DollarSign}
          label="Total revenue"
          value={money(data.total_revenue, primaryCurrency)}
          sub={revenueSub}
          color="blue"
          testId="kpi-revenue"
        />
        <KpiCard
          icon={ShoppingBag}
          label="Orders"
          value={data.total_orders.toLocaleString()}
          sub={ordersSub}
          color="purple"
          testId="kpi-orders"
        />
        <KpiCard
          icon={TrendingUp}
          label="Average order value"
          value={money(data.average_order_value, primaryCurrency)}
          sub="Revenue / orders"
          color="emerald"
          testId="kpi-aov"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{chartTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.total_orders === 0 ? (
            <div className="h-72 flex flex-col items-center justify-center text-center text-gray-500">
              <AlertTriangle className="h-8 w-8 mb-2 text-amber-400" />
              <div className="font-medium">No paid orders yet</div>
              <div className="text-sm">Charts will populate once customers complete a Stripe payment.</div>
            </div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyChart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    formatter={(v, name) => name === 'Revenue' ? money(v, primaryCurrency) : v}
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="Revenue" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Orders" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Revenue by category</CardTitle>
          </CardHeader>
          <CardContent>
            {data.category_breakdown.length === 0 ? (
              <p className="text-sm text-gray-500">No data yet.</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.category_breakdown.slice(0, 8)} layout="vertical" margin={{ left: 12, right: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#64748b" fontSize={11} />
                    <YAxis type="category" dataKey="category" stroke="#64748b" fontSize={11} width={110} />
                    <Tooltip formatter={(v) => money(v, primaryCurrency)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Currency split</CardTitle>
          </CardHeader>
          <CardContent>
            {data.currency_split.length === 0 ? (
              <p className="text-sm text-gray-500">No data yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left py-2">Currency</th>
                    <th className="text-right py-2">Orders</th>
                    <th className="text-right py-2">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.currency_split.map((c) => (
                    <tr key={c.currency} className="border-t">
                      <td className="py-2 font-medium">{c.currency}</td>
                      <td className="py-2 text-right">{c.orders.toLocaleString()}</td>
                      <td className="py-2 text-right font-semibold">{money(c.revenue, c.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Top 10 products by revenue</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.top_products.length === 0 ? (
            <p className="text-sm text-gray-500 p-4">No data yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left p-3 w-10">#</th>
                  <th className="text-left p-3">Product</th>
                  <th className="text-right p-3 w-24">Units</th>
                  <th className="text-right p-3 w-40">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.top_products.map((p, i) => (
                  <tr key={p.productId} className="border-t">
                    <td className="p-3 text-gray-500">{i + 1}</td>
                    <td className="p-3">
                      <div className="font-medium">{p.name || p.productId}</div>
                      <div className="text-xs text-gray-500">{p.productId}</div>
                    </td>
                    <td className="p-3 text-right">{p.units}</td>
                    <td className="p-3 text-right font-semibold">{money(p.revenue, primaryCurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesAdmin;
